import { onClickOutside, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { type App, nextTick, onMounted, ref, watch } from 'vue'
import AppModal from './app-modal.vue'
import AppModalDescription from './app-modal-description.vue'
import AppModalFooter from './app-modal-footer.vue'
import AppModalForm from './app-modal-form.vue'
import AppModalTitle from './app-modal-title.vue'

export function useAppModal<T extends unknown[]> (onBeforeOpen?: (...args: T) => void) {
  const modal = ref<AppModal>()
  // calling open with any arguments will be passed to onModalOpened callback
  const open = (...args: T) => {
    onBeforeOpen?.(...args)
    modal.value?.open()
  }
  const close = () => {
    modal.value?.close()
  }
  return { modal, open, close }
}

export function useModal () {
  const isOpen = ref(false)
  const modalStore = useModalStore()

  const modal = ref<HTMLDialogElement>()
  const dialogPanel = ref<HTMLElement>()
  const open = () => {
    modal.value?.showModal()
    modalStore.push(close)
    nextTick(() => { // wait for the modal to be in the DOM to allow transition
      isOpen.value = true
    })
  }

  const TRANSITION_DURATION = 200 // in ms
  const close = () => {
    isOpen.value = false
    modalStore.pop()
    setTimeout(() => {
      modal.value?.close()
    }, TRANSITION_DURATION) // Match this duration with your CSS transition time
  }

  watch(isOpen, (value) => {
    if (value) {
      onClickOutside(dialogPanel, modalStore.pop, { ignore: ['.dialog-panel'] }) // Detect click outside the dialog-panel, but ignore clicks inside any modal panel (nested modals)
    }
  })

  return { modal, isOpen, open, close, dialogPanel }
}

export function useModalEscListener () {
  const modalStore = useModalStore()
  const onEscape = useEscListener(modalStore.pop)
  onMounted(() => {
    useEventListener(document, 'keydown', onEscape)
  })
}

export const useEscListener = (callback: () => void) => {
  return (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      callback()
      event.preventDefault()
    }
  }
}

type ModalStoreState = {
  modals: { close: () => void }[],
}
const useModalStore = defineStore('Modal', {
  state: (): ModalStoreState => ({
    modals: [],
  }),
  actions: {
    push (close: () => void) {
      this.modals.push({ close })
    },
    pop () {
      this.modals.pop()?.close()
    },
  },
})

export default {
  install (app: App) {
    app.component('AppModal', AppModal)
    app.component('AppModalDescription', AppModalDescription)
    app.component('AppModalForm', AppModalForm)
    app.component('AppModalFooter', AppModalFooter)
    app.component('AppModalTitle', AppModalTitle)
  },
}

declare module 'vue' {
  export interface GlobalComponents {
    AppModal: typeof AppModal,
    AppModalDescription: typeof AppModalDescription,
    AppModalForm: typeof AppModalForm,
    AppModalFooter: typeof AppModalFooter,
    AppModalTitle: typeof AppModalTitle,
  }
}

declare global {
  type AppModal = InstanceType<typeof AppModal>
}
